<template>
  <ChannelSection v-if="hasOffers || loading">
    <template #title>
      {{ $t("channel.offers.section.title") }}
    </template>

    <template #actions>
      <DropdownMenuRoot
        v-if="channelStore.permissions.manageOffers"
        v-model:open="dropdownOpen"
      >
        <DropdownMenuTrigger as-child>
          <Button
            type="button"
            variant="icon"
            class="btn-circle border-brand-primary size-10"
            aria-label="Create new offer"
          >
            <Icon
              class="text-text size-6"
              icon="ic:baseline-plus"
              aria-hidden="true"
            />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuPortal>
          <DropdownMenuContent
            class="outline-none shadow bg-surface-secondary/90 text-text-secondary backdrop-blur-3xl p-2 rounded-xl will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade min-w-60 z-50 font-sans text-sm"
            :side-offset="5"
          >
            <DropdownMenuItem
              class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand text-text-primary data-[highlighted]:text-text-primary rounded-md px-2 py-1 gap-2 cursor-pointer"
              @click="onCreateOffer"
            >
              {{ $t("channel.offers.section.dropdown.createOffer") }}
            </DropdownMenuItem>

            <DropdownMenuItem
              class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand text-text-primary data-[highlighted]:text-text-primary rounded-md px-2 py-1 gap-2 cursor-pointer"
              @click="onManageOffers"
            >
              {{ $t("channel.offers.section.dropdown.manageOffers") }}
            </DropdownMenuItem>

            <DropdownMenuArrow
              class="fill-surface-secondary/90 backdrop-blur-3xl"
            />
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenuRoot>
    </template>

    <div v-if="!loading" class="relative">
      <ChannelRailVirtualized
        ref="railRef"
        :items="offers"
        :get-key="(offer) => offer.id"
        :item-width="312"
      >
        <template #default="{ item: { data: offer } }">
          <OfferCard
            :offer="offer"
            @open-detail="onOfferClick(offer)"
            @deleted="onOfferDeleted"
          />
        </template>
      </ChannelRailVirtualized>

      <div
        v-if="loadingMore"
        class="absolute right-0 top-0 bottom-0 flex items-center justify-center bg-gradient-to-l from-surface-primary via-surface-primary/80 to-transparent pl-8 pr-5"
      >
        <AccessibleSpinner label="Loading" size="sm" />
      </div>
    </div>

    <SkeletonRail v-else :count="3">
      <template #default="{ index }">
        <OfferCardSkeleton :index="index" />
      </template>
    </SkeletonRail>
  </ChannelSection>

  <OfferDetailModal
    v-model="isModalOpen"
    :offer="selectedOffer"
    @claimed="onOfferClaimed"
  />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useInfiniteScroll } from "@vueuse/core";
import { useChannelStore } from "@/stores/channel.store";
import { RouteNames } from "@/router/app.router";
import ChannelSection from "@/components/channel/channel-section.vue";
import ChannelRailVirtualized from "@/components/channel/channel-rail-virtualized.vue";
import SkeletonRail from "@/components/ui/skeleton/skeleton-rail.vue";
import AccessibleSpinner from "@/components/ui/accessible-spinner.vue";
import OfferCard, { type OfferCardData } from "./offer-card.vue";
import OfferCardSkeleton from "./offer-card-skeleton.vue";
import OfferDetailModal, {
  type OfferDetailModalData,
} from "./offer-detail-modal.vue";
import { Icon } from "@iconify/vue";
import {
  DropdownMenuArrow,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from "reka-ui";
import Button from "@/components/ui/button/button.vue";

const props = defineProps<{
  offers: (OfferCardData & OfferDetailModalData)[];
  loading?: boolean;
  loadingMore?: boolean;
}>();

const emit = defineEmits<{
  loadMore: [];
  refetch: [];
}>();

const channelStore = useChannelStore();
const router = useRouter();
const dropdownOpen = ref(false);
const railRef = ref<HTMLElement | null>(null);

const hasOffers = computed(() => props.offers.length > 0);

useInfiniteScroll(
  railRef,
  () => {
    emit("loadMore");
  },
  {
    direction: "right" as const,
    distance: 100,
    interval: 300,
  },
);

function onCreateOffer() {
  dropdownOpen.value = false;
  router.push({
    name: RouteNames.CreateOffer,
    params: { channel: channelStore.channelSlug },
  });
}

function onManageOffers() {
  dropdownOpen.value = false;
  router.push({
    name: RouteNames.ManageOffers,
    params: { channel: channelStore.channelSlug },
  });
}

const isModalOpen = ref(false);
const selectedOffer = ref<OfferDetailModalData | null>(null);

function onOfferClick(offer: OfferCardData & OfferDetailModalData) {
  selectedOffer.value = offer;
  isModalOpen.value = true;
}

function onOfferClaimed() {
  emit("refetch");
}

function onOfferDeleted() {
  emit("refetch");
}
</script>
